import React from "react";
import { Link } from "react-router-dom";
import PageBanner from "../../components/PageBanner";
import Breadcrumbs from "../../components/Breadcrumbs";
import JsonLd from "../../components/JsonLd";
import { birthdayPageBanner } from "../../constants/sectionBanners";
import { buildFaqPageJsonLd } from "../../utils/faqJsonLd";
import { buildBreadcrumbListJsonLd } from "../../utils/breadcrumbSchema";
import "./BirthdayEvents.css";

const BIRTHDAY_FAQ = [
  {
    question: "Do you plan kids’ birthday parties in Ahmedabad?",
    answer:
      "Yes. We design theme décor, entertainment, and catering coordination for kids’ birthdays across Ahmedabad, Gujarat, and nearby cities—with age-appropriate activities and safe setups.",
  },
  {
    question: "Can you handle milestone birthdays for adults?",
    answer:
      "We plan surprise parties, elegant dinners, and large celebrations with stage, sound, lighting, and hospitality aligned to your guest list and venue.",
  },
  {
    question: "Is Panthi Events only for birthdays?",
    answer:
      "Birthdays are one part of our wider event management services India clients use—we also link you to wedding, corporate, and production teams when you need a larger crew.",
  },
  {
    question: "What is the cost of a birthday party planner in Ahmedabad?",
    answer:
      "Pricing depends on guest count, venue, décor complexity, and entertainment. We share phased estimates after a short brief so you can prioritize must-have moments.",
  },
  {
    question: "Which is the best event management company in Gujarat for birthdays?",
    answer:
      "Choose teams with child-safe setups, clear vendor lists, and on-site managers. Panthi Events scales from home parties to banquet takeovers with the same operational discipline.",
  },
  {
    question: "Do you provide theme décor and character artists?",
    answer:
      "Yes—from balloon arches to character artists and game jockeys—we coordinate vendors who match your theme and age group.",
  },
  {
    question: "Can birthday parties include stage and LED like weddings?",
    answer:
      "For large milestones we can bring concert-grade audio and LED through our production team—ideal when you want a sangeet-style energy for a birthday bash.",
  },
  {
    question: "How far in advance should we book?",
    answer:
      "Popular weekends fill quickly—4–8 weeks ahead helps secure venues and artists; shorter timelines work when inventory allows.",
  },
];

const birthdayFaqJsonLd = buildFaqPageJsonLd(BIRTHDAY_FAQ);
const birthdayBreadcrumbLd = buildBreadcrumbListJsonLd([
  { name: "Home", path: "/" },
  { name: "Birthday Party", path: "/birthday-events" },
]);

const BirthdayEvents = () => {
  const decorServices = [
    "Invitation Card",
    "Decor Atelier",
    "Balloon Decoration",
    "Balloon Accessories",
    "Paper Decoration",
    "Centrepiece",
    "Tableware",
    "Party Favours",
  ];

  const themeServices = [
    {
      title: "Kids Theme",
      items: [
        "First Birthday",
        "Theme by Color",
        "Theme by Character",
        "Boys Theme",
        "Girls Theme",
      ],
    },
    {
      title: "Special Effects",
      items: ["Entry Concept", "Pyro & Confetti"],
    },
    {
      title: "Entertainment",
      items: ["Disc Jockey", "Projector + Screen"],
    },
    {
      title: "Catering",
      items: [
        "Fun Eatables",
        "Popcorn",
        "Cotton Candy",
        "Chocolate Fountain",
        "Sweet Corn",
        "Ice Gola",
        "Pan Cake",
      ],
    },
    {
      title: "Activities",
      items: [
        "Return Gifts",
        "Air Character",
        "Jumping",
        "Game Jockey",
        "Magic Show",
        "Puppet Show",
        "Tattoo Artist",
        "Face Painting",
        "Nail Artist",
        "Clay Modeling",
        "Tarot Reader",
        "Balloon Art",
        "Pottery",
        "Hair Braiding",
        "Hair Coloring",
        "Caricature",
        "Game Centre",
      ],
    },
  ];

  return (
    <div className="birthday-events-page">
      {birthdayBreadcrumbLd ? <JsonLd data={birthdayBreadcrumbLd} /> : null}
      {birthdayFaqJsonLd ? <JsonLd data={birthdayFaqJsonLd} /> : null}

      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Birthday Party" },
        ]}
      />

      <PageBanner
        title="Birthday Party"
        image={birthdayPageBanner}
        imageAlt="Birthday party decoration and celebration — Panthi Events, Ahmedabad, Gujarat, India"
        imageWidth={1920}
        imageHeight={1080}
      />

      <section className="section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">About Birthday Party</h2>
            <div className="title-underline"></div>
            <p className="section-subtitle">
              Making every birthday celebration special and memorable
            </p>
          </div>

          <div className="about-content">
            <p>
              From first birthdays to milestone celebrations, Panthi Events creates joyful
              birthday parties in Ahmedabad, Gujarat, and across India with décor, entertainment,
              and hospitality that match your theme and budget. As an event management company in
              Gujarat focused on family celebrations, we combine creative balloon décor, stage
              accents, and lighting so photographs look vibrant and guests feel comfortable from
              entry to cake cutting.
            </p>
            <p>
              Parents and hosts searching for the best event planner Gujarat can rely on our
              structured planning: vendor shortlists, clear timelines, and on-site managers who
              coordinate artists, games, and food counters without chaos. Whether you need a
              compact home celebration or a banquet takeover, we align sound levels, seating,
              and kids’ safety with practical layouts. Our work connects naturally with larger
              wedding planner Ahmedabad and corporate event teams when you want the same
              production quality for a private birthday.
            </p>
            <p>
              We also support Hindi and Gujarati-speaking families who want bilingual
              announcements—वेडिंग प्लानर अहमदाबाद जैसी स्पष्ट जानकारी—and ગુજરાતી માર્ગદર્શન—for
              elders and kids alike. Explore{" "}
              <Link to="/wedding-events">wedding events</Link>,{" "}
              <Link to="/corporate-events">corporate events</Link>, and{" "}
              <Link to="/production">production services</Link> when your celebration needs a
              bigger stage, LED, or concert-grade audio.
            </p>
          </div>

          <div className="decor-section">
            <h3 className="subsection-title">Decoration Services</h3>
            <div className="decor-grid">
              {decorServices.map((item, index) => (
                <div key={index} className="decor-item">
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="theme-sections">
            {themeServices.map((section, index) => (
              <div key={index} className="theme-section">
                <h3 className="subsection-title">{section.title}</h3>
                <div className="theme-items">
                  {section.items.map((item, idx) => (
                    <div key={idx} className="theme-item">
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="additional-services">
            <h3 className="subsection-title">Additional Services</h3>
            <div className="services-list">
              <div className="service-item">Kids Zone</div>
              <div className="service-item">Photography</div>
              <div className="service-item">Photo Booth</div>
              <div className="service-item">Photo Gallery</div>
            </div>
          </div>

          <div className="section-header" style={{ marginTop: "2.5rem" }}>
            <h2 className="section-title">Frequently asked questions</h2>
            <div className="title-underline"></div>
          </div>
          <dl className="about-content">
            {BIRTHDAY_FAQ.map((item, index) => (
              <div key={index} style={{ marginBottom: "1.25rem" }}>
                <dt style={{ fontWeight: 600, marginBottom: "0.35rem" }}>
                  {item.question}
                </dt>
                <dd style={{ margin: 0 }}>{item.answer}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </div>
  );
};

export default BirthdayEvents;
